import { ComboboxButton, ComboboxOption, ComboboxOptions, Field } from '@headlessui/react'
import {
  ArrowButton,
  CloseButton,
  ComboboxSinge,
  FieldErrorAndDescription,
  FieldLabel,
  FormSelectDataType,
  FormSelectIdType,
  FormSelectInput,
  FormSelectProps,
  ListOptions,
  getSelectedData,
  getVirtualizedData,
  useFilteredData,
} from '@systemeio/ui'
import { useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface FormSelectWithCreateProps<T extends FormSelectIdType> extends FormSelectProps<T> {
  createData: {
    getCustomCaption: (value: string) => string
    onCreate: (data: string) => Promise<void>
  }
}

const CREATE_DATA_KEY = '__create-data-key'

export function FormSelectWithCreate<T extends FormSelectIdType>({
  label,
  data,
  staticData,
  onChange,
  labelClassName,
  value,
  isPreFetching,
  placeholder,
  error,
  required,
  description,
  className,
  containerClassName,
  closeButtonClassName,
  arrowButtonClassName,
  fieldClassName,
  disabled,
  withoutCloseIcon,
  onOpen,
  onClose,
  dataTest,
  wrapperProps,
  loaderClassName,
  loaderWrapperClassName,
  inputWrapperClassName,
  inputRef,
  createData,
  ...optionsProps
}: FormSelectWithCreateProps<T>) {
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    if (opened && onOpen) {
      onOpen()
      setOpened(false)
    }
  }, [opened])

  const selectedData = getSelectedData(value, data ?? staticData) ?? null

  const { filteredData, query, setQuery } = useFilteredData(data)

  const hasValue = value !== undefined

  const noData = !data && !staticData

  const onlyValue = hasValue && noData

  const isDisabled = disabled || isPreFetching || onlyValue

  const virtual = useMemo(() => {
    const virtualData = getVirtualizedData(filteredData)
    if (!virtualData) return

    const hasCreatDataItem = virtualData.options.some(
      (el: FormSelectDataType<T>) => el.id === CREATE_DATA_KEY,
    )

    if (!hasCreatDataItem && !!query) {
      virtualData.options.unshift({
        id: CREATE_DATA_KEY,
        caption: query,
        customCaption: createData.getCustomCaption(query),
      })
    }
    return virtualData
  }, [createData, filteredData, query])

  const showCloseButton = hasValue && !withoutCloseIcon && !isDisabled

  return (
    <div className={twMerge('relative flex flex-col gap-1', containerClassName)}>
      <ComboboxSinge
        selectedData={selectedData}
        virtual={virtual}
        onChange={async data => {
          onChange(data)
          if (data === CREATE_DATA_KEY) {
            await createData.onCreate(query)
          }
        }}
        disabled={isDisabled}
        onClose={() => {
          setQuery('')
          onClose?.()
        }}
      >
        {open => (
          <>
            <Field className={twMerge('relative flex flex-col gap-1', fieldClassName)}>
              <FieldLabel
                wrapperAs={'label'}
                wrapperProps={wrapperProps}
                label={label}
                required={required}
                labelClassName={twMerge('text-sm font-medium', labelClassName)}
              />
              <ComboboxButton
                as="button"
                className={twMerge('relative', inputWrapperClassName)}
              >
                <FormSelectInput
                  dataTest={dataTest}
                  inputRef={inputRef}
                  isDisabled={isDisabled}
                  hasError={!!error}
                  open={open}
                  className={className}
                  placeholder={placeholder}
                  selectedCaption={selectedData?.caption ?? ''}
                  isPreFetching={isPreFetching}
                  onFocus={() => setOpened(true)}
                  onChange={value => {
                    setOpened(true)
                    setQuery(value)
                  }}
                />
                <ArrowButton
                  isDisabled={isDisabled}
                  arrowButtonClassName={twMerge(
                    'absolute inset-y-0 right-4 h-[46px] sm:h-[42px]',
                    arrowButtonClassName,
                  )}
                  open={open}
                />
              </ComboboxButton>
              {showCloseButton && (
                <CloseButton
                  isDisabled={isDisabled}
                  closeButtonClassName={twMerge(
                    'absolute bottom-0 right-10 h-[46px] sm:h-[42px]',
                    closeButtonClassName,
                  )}
                  onClick={() => onChange(undefined)}
                />
              )}
            </Field>
            <ListOptions
              asOptions={ComboboxOptions}
              asOption={ComboboxOption}
              data={filteredData}
              {...optionsProps}
            />
          </>
        )}
      </ComboboxSinge>
      <FieldErrorAndDescription error={error} description={description} />
    </div>
  )
}
